import { onCLS, onFCP, onINP, onLCP, onTTFB } from "web-vitals/attribution";
import { PerformanceHubContext, WebVitalsConfig } from "./types";

const queue = new Set();

interface Metric {
  name: string;
  value: number;
  rating: number;
  attribution?: Attribution;
}

interface Attribution {
  debugTarget?: string;
  delta?: number;
  inputDelay?: number;
  processingDuration?: number;
}

function addToQueue(metric: any) {
  queue.add(metric);
}

function getConfig(): WebVitalsConfig {
  return window?.performanceHub?.config?.webVitals;
}

function getContext(): PerformanceHubContext {
  return window?.performanceHub?.context;
}

function processMetric(metric): Metric {
  const attribution: Attribution = {};
  const config = getConfig();

  if (
    config &&
    config?.attribution?.enabledFor?.includes(metric.name) &&
    config?.attribution?.ratings?.includes(metric.rating)
  ) {
    switch (metric.name) {
      case "CLS":
        attribution.debugTarget = metric.attribution.largestShiftTarget;
        attribution.delta = metric.delta;
        break;
      case "INP":
        attribution.debugTarget = metric.attribution.interactionTarget;
        attribution.delta = metric.delta;
        attribution.inputDelay = metric.attribution.inputDelay;
        attribution.processingDuration = metric.attribution.processingDuration;
        break;
      case "LCP":
        attribution.debugTarget = metric.attribution.target;
        break;
      default:
        break;
    }
  }

  return {
    name: metric.name,
    value: metric.value,
    rating: metric.rating,
    attribution: attribution,
  };
}

function flushQueue() {
  if (queue.size > 0) {
    // Replace with whatever serialization method you prefer.
    // Note: JSON.stringify will likely include more data than you need.
    const metrics = [...queue].map(processMetric);
    const config = getConfig();
    const context = getContext();
    if (!config || !context?.dealerId || !context.pageType) {
      console.log(
        "No context or dealerId or pageType. Skipping web vital data send."
      );
      return;
    }
    const body = {
      url: window.location.href,
      metrics: metrics,
      dealerId: context.dealerId,
      pageType: context.pageType,
    };
    const bodyString = JSON.stringify(body);
    const apiEndpoint = config?.apiEndpoint;
    if (apiEndpoint) {
      fetch(apiEndpoint, {
        method: "POST",
        body: bodyString,
        headers: {
          "Content-Type": "application/json",
        },
      });
    }
    queue.clear();
  }
}

function onTBT(addToQueue) {
  const config = getConfig();
  if (config?.enableTBTTracking) {
    let totalBlockingTime = 0;
    const event = {
      name: "TBT",
      value: 0,
      rating: "good",
    };
    if (!PerformanceObserver) {
      return;
    }
    const observer = new PerformanceObserver(function (list) {
      const perfEntries = list.getEntries();
      for (const perfEntry of perfEntries) {
        totalBlockingTime += perfEntry.duration - 50;
      }
      event.value = totalBlockingTime;
      if (totalBlockingTime < 200) {
        event.rating = "good";
      } else if (totalBlockingTime >= 200 && totalBlockingTime < 500) {
        event.rating = "needs-improvement";
      } else if (totalBlockingTime >= 500) {
        event.rating = "poor";
      }
      addToQueue(event);
    });
    observer.observe({ type: "longtask", buffered: true });
  }
}

onCLS(addToQueue);
onINP(addToQueue);
onLCP(addToQueue);
onFCP(addToQueue);
onTTFB(addToQueue);
onTBT(addToQueue);

// Report all available metrics whenever the page is backgrounded or unloaded.
addEventListener("visibilitychange", () => {
  if (document.visibilityState === "hidden") {
    flushQueue();
  }
});

// NOTE: Safari does not reliably fire the `visibilitychange` event when the
// page is being unloaded. If Safari support is needed, you should also flush
// the queue in the `pagehide` event.
window.addEventListener("pagehide", flushQueue);
